// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import { useAffiliatePayments, useAffiliateEarningsSummary } from "../hooks/useAffiliatePayments";
import {
  useAffiliatePayoutCurrentMonth,
  useAffiliatePayoutPeriodEvents,
  useAffiliatePayoutPeriods,
  useAffiliatePayoutStatus,
} from "../hooks/useAffiliatePayouts";
import PayoutSetupReminder from "../components/PayoutSetupReminder";
import {
  formatIsoDateTime,
  formatMoney,
  formatMonthLabel,
  formatPlanLabel,
} from "../utils/affiliatePaymentFormat";
import { getPayoutStatusBadgeClasses, getPayoutStatusMeta } from "../utils/affiliatePayoutStatusMeta";
import {
  affiliateBrandBar,
  affiliateSpinnerBorder,
} from "../utils/uiClasses";

const PERIODS_PAGE_SIZE = 12;
const PAYMENTS_PAGE_SIZE = 20;

const StatCard = ({ label, value, hint }) => (
  <div
    className={`relative overflow-hidden rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-5 sm:p-6 shadow-sm`}
  >
    <div className={`absolute inset-x-0 top-0 h-1 ${affiliateBrandBar}`} />
    <p className={`${typography.label} ${textColors.secondary}`}>{label}</p>
    <p className={`mt-2 ${typography.h1} ${textColors.primary} tracking-tight`}>{value}</p>
    {hint ? <p className={`mt-2 ${typography.caption} ${textColors.muted}`}>{hint}</p> : null}
  </div>
);

const StatCardSkeleton = () => (
  <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-5 sm:p-6 shadow-sm`}>
    <div className="animate-pulse space-y-3">
      <div className={`h-4 ${surfaces.tertiary} rounded w-1/2`} />
      <div className={`h-8 ${surfaces.tertiary} rounded w-2/3`} />
      <div className={`h-3 ${surfaces.tertiary} rounded w-3/4`} />
    </div>
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={getPayoutStatusBadgeClasses(status)}>{getPayoutStatusMeta(status).label}</span>
);

const ACTOR_TYPE_LABELS = { system: "System", admin: "Admin", affiliate: "You" };

/** Lazy status-history panel — mounted only when a history row is expanded. */
const PeriodStatusHistory = ({ periodId }) => {
  const eventsQuery = useAffiliatePayoutPeriodEvents({ periodId, enabled: true });

  if (eventsQuery.isLoading) {
    return (
      <div className="flex items-center gap-3 py-3">
        <div className={`animate-spin rounded-full h-5 w-5 border-b-2 ${affiliateSpinnerBorder}`} />
        <span className={`${typography.bodySmall} ${textColors.muted}`}>Loading status history…</span>
      </div>
    );
  }

  if (eventsQuery.error) {
    return (
      <p className={`${typography.bodySmall} text-red-600 dark:text-red-400 py-3`}>
        Failed to load status history: {eventsQuery.error?.message || "Unknown error"}
      </p>
    );
  }

  const events = eventsQuery.data?.events || [];
  if (events.length === 0) {
    return <p className={`${typography.bodySmall} ${textColors.muted} py-3`}>No status changes recorded yet.</p>;
  }

  return (
    <div className="py-2">
      <p className={`${typography.label} ${textColors.secondary} mb-2`}>Status history</p>
      <ul className="space-y-2">
        {events.map((event, index) => (
          <li key={`${event.created_at}-${index}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className={`${typography.caption} ${textColors.muted} whitespace-nowrap`}>
              {formatIsoDateTime(event.created_at)}
            </span>
            <span className={`${typography.bodySmall} ${textColors.primary}`}>
              {event.from_status
                ? `${getPayoutStatusMeta(event.from_status).label} → ${getPayoutStatusMeta(event.to_status).label}`
                : `Created as ${getPayoutStatusMeta(event.to_status).label}`}
            </span>
            <span className={`${typography.caption} ${textColors.tertiary}`}>
              by {ACTOR_TYPE_LABELS[event.actor_type] || event.actor_type}
            </span>
            {event.note ? (
              <span className={`${typography.caption} ${textColors.secondary} w-full`}>Note: {event.note}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

const periodTableHeaderCell = `px-4 py-3 text-left ${typography.label} ${textColors.secondary}`;
const periodTableHeaderCellRight = `px-4 py-3 text-right ${typography.label} ${textColors.secondary}`;

const MyPaymentsPage = () => {
  const [searchParams] = useSearchParams();
  const onboardingParam = searchParams.get("onboarding");

  const statusQuery = useAffiliatePayoutStatus();
  const currentMonthQuery = useAffiliatePayoutCurrentMonth();
  const earningsQuery = useAffiliateEarningsSummary({ months: 6, enabled: true });

  // Returning from Stripe onboarding (?onboarding=return|refresh): re-check the
  // Express account state so the setup section reflects the latest status.
  const refetchStatus = statusQuery.refetch;
  useEffect(() => {
    if (onboardingParam === "return" || onboardingParam === "refresh") {
      refetchStatus();
    }
  }, [onboardingParam, refetchStatus]);

  const account = statusQuery.data?.account || null;
  const isPayoutReady = !!account?.is_payout_ready;

  // History: paginated with "Load more" (offset increments of the page size,
  // appending rows) — same pattern as the referral ledger below.
  const [periodsOffset, setPeriodsOffset] = useState(0);
  const [periodRows, setPeriodRows] = useState([]);
  const [periodsTotalCount, setPeriodsTotalCount] = useState(null);
  const [expandedPeriodIds, setExpandedPeriodIds] = useState(() => new Set());

  const periodsQuery = useAffiliatePayoutPeriods({
    limit: PERIODS_PAGE_SIZE,
    offset: periodsOffset,
    enabled: true,
  });

  useEffect(() => {
    const data = periodsQuery.data;
    if (!data) return;
    setPeriodRows((prev) => [...prev.slice(0, periodsOffset), ...(data.periods || [])]);
    setPeriodsTotalCount(data.total_count ?? 0);
  }, [periodsQuery.data, periodsOffset]);

  const hasMorePeriods = periodsTotalCount != null && periodRows.length < periodsTotalCount;
  const isPeriodsInitialLoading = periodsQuery.isLoading && periodRows.length === 0;
  const isPeriodsLoadingMore = periodsQuery.isFetching && periodsOffset > 0;

  const togglePeriodExpanded = (periodId) => {
    setExpandedPeriodIds((prev) => {
      const next = new Set(prev);
      if (next.has(periodId)) {
        next.delete(periodId);
      } else {
        next.add(periodId);
      }
      return next;
    });
  };

  // Referral ledger: anonymized payments from referred users (moved from Analytics).
  const [paymentsOffset, setPaymentsOffset] = useState(0);
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentsMeta, setPaymentsMeta] = useState(null); // { totalCount, totals }

  const paymentsQuery = useAffiliatePayments({
    limit: PAYMENTS_PAGE_SIZE,
    offset: paymentsOffset,
    enabled: true,
  });

  useEffect(() => {
    const data = paymentsQuery.data;
    if (!data) return;
    setPaymentRows((prev) => [...prev.slice(0, paymentsOffset), ...(data.payments || [])]);
    setPaymentsMeta({ totalCount: data.total_count ?? 0, totals: data.totals || null });
  }, [paymentsQuery.data, paymentsOffset]);

  const paymentsTotalCount = paymentsMeta?.totalCount ?? 0;
  const paymentsTotals = paymentsMeta?.totals || null;
  const hasMorePayments = paymentsMeta != null && paymentRows.length < paymentsTotalCount;
  const isPaymentsInitialLoading = paymentsQuery.isLoading && paymentRows.length === 0;
  const isPaymentsLoadingMore = paymentsQuery.isFetching && paymentsOffset > 0;

  const currentMonthPeriod = currentMonthQuery.data?.period ?? null;
  const earningsCurrentMonth = earningsQuery.data?.current_month || null;
  const showPayoutAccountCard =
    statusQuery.isLoading || !!statusQuery.error || isPayoutReady;

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Single setup CTA on this tab (shell banner is hidden on /payments) —
            placed before the title, same as every other tab's shell banner. */}
        <PayoutSetupReminder embedded showPaymentsLink={false} />

        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>My Payments</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>
            Your commission estimates, payout history, and payments from your referred users.
          </p>
        </div>

        {/* Status card when ready / loading / error — setup CTA lives in the banner above. */}
        {showPayoutAccountCard ? (
          <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
            <h3 className={`${typography.h4} ${textColors.primary} mb-1`}>Payout account</h3>
            <p className={`${typography.caption} ${textColors.muted} mb-4`}>
              Commissions are paid to your Stripe Express account.
            </p>

            {statusQuery.isLoading && (
              <div className="flex items-center gap-3 py-2">
                <div className={`animate-spin rounded-full h-5 w-5 border-b-2 ${affiliateSpinnerBorder}`} />
                <span className={`${typography.bodySmall} ${textColors.muted}`}>Checking payout account…</span>
              </div>
            )}

            {statusQuery.error && (
              <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
                Failed to load payout account status: {statusQuery.error?.message || "Unknown error"}
              </p>
            )}

            {!statusQuery.isLoading && !statusQuery.error && isPayoutReady && (
              <div className="space-y-1">
                <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                  Payouts ready
                </span>
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  Your Stripe Express account is set up and can receive payouts.
                </p>
              </div>
            )}
          </div>
        ) : null}

        {/* Earnings summary cards (moved from Analytics) */}
        {earningsQuery.isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </div>
        )}

        {earningsQuery.error && (
          <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
            <p className={`${typography.body} ${textColors.secondary}`}>Failed to load earnings summary</p>
            <p className={`${typography.bodySmall} ${textColors.muted} mt-1`}>
              {earningsQuery.error?.message || "Unknown error"}
            </p>
          </div>
        )}

        {!earningsQuery.isLoading && !earningsQuery.error && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Gross Affiliated Payments"
              value={earningsCurrentMonth ? formatMoney(earningsCurrentMonth.gross_amount) : "—"}
              hint={
                earningsCurrentMonth
                  ? `Payments from your referred users in ${formatMonthLabel(earningsCurrentMonth.month)}`
                  : "No earnings data yet for the current month"
              }
            />
            <StatCard
              label="Commission Rate"
              value={earningsCurrentMonth ? `${earningsCurrentMonth.commission_percent}%` : "—"}
              hint={
                earningsCurrentMonth
                  ? `${earningsCurrentMonth.new_paying_referrals} new paying referral${
                      earningsCurrentMonth.new_paying_referrals === 1 ? "" : "s"
                    } this month`
                  : "No earnings data yet for the current month"
              }
            />
            <StatCard
              label="Estimated Commission"
              value={earningsCurrentMonth ? formatMoney(earningsCurrentMonth.estimated_commission) : "—"}
              hint={
                earningsCurrentMonth
                  ? `Estimated for ${formatMonthLabel(earningsCurrentMonth.month)}`
                  : "No earnings data yet for the current month"
              }
            />
          </div>
        )}

        {/* Current month (live estimate) */}
        <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
          <h3 className={`${typography.h4} ${textColors.primary} mb-1`}>Current month</h3>
          <p className={`${typography.caption} ${textColors.muted} mb-4`}>
            Live estimate for the open calendar month — the amount may change until the month closes.
          </p>

          {currentMonthQuery.isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${affiliateSpinnerBorder}`} />
            </div>
          )}

          {currentMonthQuery.error && (
            <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
              Failed to load current month estimate: {currentMonthQuery.error?.message || "Unknown error"}
            </p>
          )}

          {!currentMonthQuery.isLoading && !currentMonthQuery.error && !currentMonthPeriod && (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <p className={`${typography.body} ${textColors.secondary}`}>No commission activity yet this month.</p>
              <p className={`${typography.caption} ${textColors.muted}`}>
                Your live estimate appears here once a referred user makes a payment.
              </p>
            </div>
          )}

          {!currentMonthQuery.isLoading && !currentMonthQuery.error && currentMonthPeriod && (
            <div className={`${surfaces.primary} rounded-lg overflow-hidden ${borders.default} border`}>
              <div className="overflow-x-auto">
                <table className="min-w-full w-full" aria-label="Current month commission estimate table">
                  <thead className={`${surfaces.secondary} ${borders.default} border-b`}>
                    <tr>
                      <th className={periodTableHeaderCell}>Month</th>
                      <th className={periodTableHeaderCellRight}>Gross</th>
                      <th className={periodTableHeaderCellRight}>New referrals</th>
                      <th className={periodTableHeaderCellRight}>Tier %</th>
                      <th className={periodTableHeaderCellRight}>Estimated commission</th>
                      <th className={periodTableHeaderCell}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} whitespace-nowrap`}>
                        {formatMonthLabel(currentMonthPeriod.label)}
                      </td>
                      <td className={`px-4 py-3 ${typography.body} ${textColors.primary} text-right whitespace-nowrap`}>
                        {formatMoney(currentMonthPeriod.gross_amount, currentMonthPeriod.currency)}
                      </td>
                      <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} text-right`}>
                        {currentMonthPeriod.new_referral_count}
                      </td>
                      <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} text-right`}>
                        {currentMonthPeriod.commission_percent}%
                      </td>
                      <td className={`px-4 py-3 ${typography.body} ${textColors.primary} text-right whitespace-nowrap`}>
                        {formatMoney(currentMonthPeriod.commission_amount, currentMonthPeriod.currency)}
                      </td>
                      <td className={`px-4 py-3`}>
                        <StatusBadge status={currentMonthPeriod.status} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* History (frozen periods) */}
        <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
          <h3 className={`${typography.h4} ${textColors.primary} mb-1`}>History</h3>
          <p className={`${typography.caption} ${textColors.muted} mb-4`}>
            Closed commission months with frozen amounts, newest first.
          </p>

          {isPeriodsInitialLoading && (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${affiliateSpinnerBorder}`} />
            </div>
          )}

          {periodsQuery.error && periodRows.length === 0 && !periodsQuery.isLoading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className={`${typography.body} ${textColors.secondary}`}>Failed to load payout history</p>
              <p className={`${typography.bodySmall} ${textColors.muted}`}>
                {periodsQuery.error?.message || "Unknown error"}
              </p>
            </div>
          )}

          {!isPeriodsInitialLoading && !periodsQuery.error && periodRows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className={`${typography.body} ${textColors.secondary}`}>No closed commission months yet.</p>
              <p className={`${typography.caption} ${textColors.muted}`}>
                When a calendar month with commission activity ends, it appears here with its payout status.
              </p>
            </div>
          )}

          {periodRows.length > 0 && (
            <div className="space-y-4">
              <div className={`${surfaces.primary} rounded-lg overflow-hidden ${borders.default} border`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full w-full" aria-label="Commission payout history table">
                    <thead className={`${surfaces.secondary} ${borders.default} border-b`}>
                      <tr>
                        <th className={periodTableHeaderCell}>Month</th>
                        <th className={periodTableHeaderCellRight}>Gross</th>
                        <th className={periodTableHeaderCellRight}>New referrals</th>
                        <th className={periodTableHeaderCellRight}>Tier %</th>
                        <th className={periodTableHeaderCellRight}>Commission</th>
                        <th className={periodTableHeaderCell}>Status</th>
                        <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>
                          <span className="sr-only">Status history</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodRows.map((period) => {
                        const isExpanded = expandedPeriodIds.has(period.id);
                        return (
                          <React.Fragment key={period.id}>
                            <tr className={`${borders.default} border-b`}>
                              <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} whitespace-nowrap`}>
                                {formatMonthLabel(period.label)}
                              </td>
                              <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} text-right whitespace-nowrap`}>
                                {formatMoney(period.gross_amount, period.currency)}
                              </td>
                              <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} text-right`}>
                                {period.new_referral_count}
                              </td>
                              <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} text-right`}>
                                {period.commission_percent}%
                              </td>
                              <td className={`px-4 py-3 ${typography.body} ${textColors.primary} text-right whitespace-nowrap`}>
                                {formatMoney(period.commission_amount, period.currency)}
                              </td>
                              <td className={`px-4 py-3`}>
                                <StatusBadge status={period.status} />
                                {period.status === "rejected" && period.review_note ? (
                                  <p className={`mt-1 ${typography.caption} ${textColors.secondary}`}>
                                    Note: {period.review_note}
                                  </p>
                                ) : null}
                                {period.status === "failed" && period.failure_reason ? (
                                  <p className={`mt-1 ${typography.caption} text-red-600 dark:text-red-400`}>
                                    {period.failure_reason}
                                  </p>
                                ) : null}
                              </td>
                              <td className={`px-4 py-3 text-right`}>
                                <button
                                  type="button"
                                  onClick={() => togglePeriodExpanded(period.id)}
                                  aria-expanded={isExpanded}
                                  className={`px-3 py-1 rounded ${typography.button} ${textColors.primary} ${surfaces.tertiary} ${surfaces.hover} transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 whitespace-nowrap`}
                                >
                                  {isExpanded ? "Hide history" : "Show history"}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className={`${borders.default} border-b ${surfaces.secondary}`}>
                                <td colSpan={7} className="px-4">
                                  <PeriodStatusHistory periodId={period.id} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                {hasMorePeriods && (
                  <button
                    type="button"
                    onClick={() => setPeriodsOffset(periodRows.length)}
                    disabled={periodsQuery.isFetching}
                    className={`px-4 py-2 rounded-lg ${typography.button} ${textColors.primary} ${surfaces.tertiary} ${surfaces.hover} ${borders.default} border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isPeriodsLoadingMore ? "Loading…" : "Load more"}
                  </button>
                )}
                {periodsTotalCount != null && (
                  <p className={`${typography.caption} ${textColors.muted}`}>
                    Showing {periodRows.length} of {periodsTotalCount} month
                    {periodsTotalCount === 1 ? "" : "s"}
                  </p>
                )}
              </div>

              {periodsQuery.error && (
                <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
                  Failed to load more history: {periodsQuery.error?.message || "Unknown error"}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Referral ledger (moved from Analytics) */}
        <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
          <h3 className={`${typography.h4} ${textColors.primary} mb-1`}>Recent payments from referred users</h3>
          <p className={`${typography.caption} ${textColors.muted} mb-4`}>
            Anonymized payments from your referred users that feed your monthly commission.
          </p>

          {isPaymentsInitialLoading && (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${affiliateSpinnerBorder}`} />
            </div>
          )}

          {paymentsQuery.error && paymentRows.length === 0 && !paymentsQuery.isLoading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className={`${typography.body} ${textColors.secondary}`}>Failed to load payments</p>
              <p className={`${typography.bodySmall} ${textColors.muted}`}>
                {paymentsQuery.error?.message || "Unknown error"}
              </p>
            </div>
          )}

          {!isPaymentsInitialLoading && !paymentsQuery.error && paymentRows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className={`${typography.body} ${textColors.secondary}`}>No payments yet.</p>
              <p className={`${typography.caption} ${textColors.muted}`}>
                Payments from your referred users will appear here.
              </p>
            </div>
          )}

          {paymentRows.length > 0 && (
            <div className="space-y-4">
              <div className={`${surfaces.primary} rounded-lg overflow-hidden ${borders.default} border`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full w-full" aria-label="Recent affiliate payments table">
                    <thead className={`${surfaces.secondary} ${borders.default} border-b`}>
                      <tr>
                        <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>Date</th>
                        <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>
                          Amount
                        </th>
                        <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>Plan</th>
                        <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                          Coupon
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentRows.map((payment, index) => (
                        <tr key={`${payment.paid_at || "payment"}-${index}`} className={`${borders.default} border-b`}>
                          <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} whitespace-nowrap`}>
                            {formatIsoDateTime(payment.paid_at)}
                          </td>
                          <td className={`px-4 py-3 ${typography.body} ${textColors.primary} text-right whitespace-nowrap`}>
                            {formatMoney(payment.amount, payment.currency)}
                          </td>
                          <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary}`}>
                            {formatPlanLabel(payment.plan_name, payment.billing_period)}
                          </td>
                          <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary}`}>
                            {payment.coupon_code || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {paymentsTotals && (
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                  <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                    Period gross:{" "}
                    <span className={`${textColors.primary} font-medium`}>
                      {formatMoney(paymentsTotals.gross_amount, paymentsTotals.currency)}
                    </span>
                  </p>
                  <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                    Estimated commission:{" "}
                    <span className={`${textColors.primary} font-medium`}>
                      {formatMoney(paymentsTotals.estimated_commission, paymentsTotals.currency)}
                    </span>
                  </p>
                  <p className={`${typography.caption} ${textColors.muted}`}>
                    Showing {paymentRows.length} of {paymentsTotalCount} payment
                    {paymentsTotalCount === 1 ? "" : "s"}
                  </p>
                </div>
              )}

              {paymentsQuery.error && (
                <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
                  Failed to load more payments: {paymentsQuery.error?.message || "Unknown error"}
                </p>
              )}

              {hasMorePayments && (
                <button
                  type="button"
                  onClick={() => setPaymentsOffset(paymentRows.length)}
                  disabled={paymentsQuery.isFetching}
                  className={`px-4 py-2 rounded-lg ${typography.button} ${textColors.primary} ${surfaces.tertiary} ${surfaces.hover} ${borders.default} border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isPaymentsLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyPaymentsPage;
